import React, { useState } from "react";
import { useAuthContext } from "contexts/auth";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { Button } from "components/button";

const FileDownload = () => {
	const auth = useAuthContext();
	const history = useHistory();
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [downloadOptions, setDownloadOptions] = useState({
		customer: false,
		request: false,
		property: false,
	});

	// Check if user is owner
	React.useEffect(() => {
		if (!auth.owner) {
			history.replace("/nopermission");
		}
	}, [auth.owner, history]);

	const handleCheckboxChange = (option: "customer" | "request" | "property") => {
		setDownloadOptions((prev) => ({
			...prev,
			[option]: !prev[option],
		}));
	};

	const handleSendEmail = async () => {
		setIsLoading(true);
		setMessage("");

		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin/send-owner-file-download-email`,
				{
					downloadOptions,
				}
			);

			if (response.status === 200) {
				setMessage("Email sent successfully!");
			}
		} catch (error) {
			console.error("Error sending email:", error);
			setMessage("Failed to send email. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	// Don't render if not owner
	if (!auth.owner) {
		return null;
	}

	return (
		<div className="p-6 max-w-4xl">
			<h1 className="text-2xl font-bold mb-4">File Download</h1>
			<p className="text-gray-600 mb-6">
				This page is only accessible to the owner. Use the button below to send an email
				notification.
			</p>

		<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
			<div className="mb-6">
				<h2 className="text-lg font-semibold mb-2">Send Email Notification</h2>
				<p className="text-gray-600 text-sm mb-4">
					Select the file types to include in the download, then click the button to send an
					email notification to the owner's email address.
				</p>
			</div>

			<div className="mb-6">
				<h3 className="text-sm font-semibold text-gray-700 mb-3">File Types to Include:</h3>
				<div className="space-y-3">
					<label className="flex items-center space-x-3 cursor-pointer">
						<input
							type="checkbox"
							checked={downloadOptions.customer}
							onChange={() => handleCheckboxChange("customer")}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
						<div className="flex-1">
							<span className="text-sm font-medium text-gray-900">Customer Files</span>
							<p className="text-xs text-gray-500">
								Include all files associated with customers
							</p>
						</div>
					</label>

					<label className="flex items-center space-x-3 cursor-pointer">
						<input
							type="checkbox"
							checked={downloadOptions.request}
							onChange={() => handleCheckboxChange("request")}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
						<div className="flex-1">
							<span className="text-sm font-medium text-gray-900">Request Files</span>
							<p className="text-xs text-gray-500">
								Include all files associated with requests
							</p>
						</div>
					</label>

					<label className="flex items-center space-x-3 cursor-pointer">
						<input
							type="checkbox"
							checked={downloadOptions.property}
							onChange={() => handleCheckboxChange("property")}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
						<div className="flex-1">
							<span className="text-sm font-medium text-gray-900">Property Files</span>
							<p className="text-xs text-gray-500">
								Include all files associated with properties
							</p>
						</div>
					</label>
				</div>

				{!downloadOptions.customer && !downloadOptions.request && !downloadOptions.property && (
					<div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
						<p className="text-xs text-blue-800">
							<strong>Note:</strong> If no options are selected, all file types will be
							included in the download.
						</p>
					</div>
				)}
			</div>

			<div className="flex items-center gap-4">
				<Button primary onClick={handleSendEmail} disabled={isLoading}>
					{isLoading ? "Sending..." : "Send Email to Owner"}
				</Button>
			</div>

				{message && (
					<div
						className={`mt-4 p-3 rounded ${
							message.includes("success")
								? "bg-green-50 text-green-800 border border-green-200"
								: "bg-red-50 text-red-800 border border-red-200"
						}`}
					>
						{message}
					</div>
				)}

				<div className="mt-6 pt-6 border-t border-gray-200">
					<h3 className="text-sm font-semibold text-gray-700 mb-2">Owner Information</h3>
					<div className="text-sm text-gray-600">
						<p>
							<span className="font-medium">Name:</span> {auth.name}
						</p>
						<p>
							<span className="font-medium">User ID:</span> {auth.user_id}
						</p>
						<p>
							<span className="font-medium">Company ID:</span> {auth.company_id}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FileDownload;

